import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Notification {
  id: string;
  type: 'feedback' | 'interaction' | 'connection' | 'goal' | 'reminder' | 'partner' | 'training_reminder';
  title: string;
  content: string;
  metadata: Record<string, any>;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
  action_url: string | null;
  target_id: string | null;
  target_type: string | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
}

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Query for unread notifications count
  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useQuery({
    queryKey: ['notifications', 'unread-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { data, error } = await supabase
        .rpc('get_unread_notifications_count', { p_user_id: user.id });

      if (error) {
        console.error('Error fetching unread count:', error);
        return 0;
      }

      return data || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Query for user notifications with pagination
  const {
    data: notifications = [],
    isLoading,
    error,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: ['notifications', 'list', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .rpc('get_user_notifications', {
          p_user_id: user.id,
          p_limit: 50,
          p_offset: 0,
          p_only_unread: false,
        });

      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user?.id,
  });

  // Mutation to mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('No user ID');

      const { data, error } = await supabase
        .rpc('mark_notifications_as_read', { p_user_id: user.id });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list', user?.id] });
    },
  });

  // Mutation to mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error('No user ID');

      const { data, error } = await supabase
        .rpc('mark_notification_as_read', {
          p_notification_id: notificationId,
          p_user_id: user.id,
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list', user?.id] });
    },
  });

  // Function to open notifications screen
  const openNotifications = useCallback(() => {
    setIsOpen(true);
    // Mark all as read when opening
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  // Function to close notifications screen
  const closeNotifications = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Function to handle notification click with contextual navigation
  const handleNotificationClick = useCallback(async (notification: Notification) => {
    try {
      // Mark as read if not already read
      if (!notification.is_read) {
        markAsReadMutation.mutate(notification.id);
      }

      // Handle navigation based on notification type and metadata
      switch (notification.type) {
        case 'feedback':
          // Navigate to profile with recognitions tab
          navigate('/profile', {
            state: {
              fromNotification: true,
              activeTab: 'recognitions',
              highlightId: notification.metadata.feedback_id,
              trainingId: notification.metadata.training_id
            }
          });
          break;

        case 'connection':
          // Navigate to user connections page - use the current user's ID
          if (user?.id) {
            navigate(`/user/connections/${user.id}`, {
              state: {
                fromNotification: true,
                highlightId: notification.metadata.connection_id
              }
            });
          }
          break;

        case 'interaction':
          // Navigate to post page
          if (notification.metadata.post_id) {
            navigate(`/post/${notification.metadata.post_id}`, {
              state: {
                fromNotification: true,
                commentId: notification.metadata.comment_id
              }
            });
          }
          break;

        case 'goal':
          // Navigate to Practitioner Home
          navigate('/home', {
            state: {
              fromNotification: true,
              highlightGoal: notification.metadata.goal_id
            }
          });
          break;

        case 'reminder':
          // Navigate to Calendar and open training details modal
          navigate('/calendar', {
            state: {
              fromNotification: true,
              openTrainingModal: true,
              trainingId: notification.metadata.training_id,
              highlightDate: notification.metadata.training_date
            }
          });
          break;

        case 'training_reminder':
          // Navigate to Calendar and open training details modal
          navigate('/calendar', {
            state: {
              fromNotification: true,
              openTrainingModal: true,
              trainingId: notification.metadata.training_id,
              highlightDate: notification.metadata.training_date
            }
          });
          break;

        case 'partner':
          // Navigate to user's Calendar
          navigate('/calendar', {
            state: {
              fromNotification: true,
              highlightTrainingId: notification.metadata.training_id,
              fromPartnerAddition: true
            }
          });
          break;

        default:
          // Fallback to home if notification type is not recognized
          navigate('/home', {
            state: {
              fromNotification: true,
              notificationType: notification.type
            }
          });
          break;
      }

      // Close notifications screen after navigation
      closeNotifications();

    } catch (error) {
      console.error('Error handling notification click:', error);
      // Fallback: navigate to home
      navigate('/home');
    }
  }, [markAsReadMutation, navigate, closeNotifications, user?.id]);

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch data when new notification is inserted
          refetchUnreadCount();
          refetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, refetchUnreadCount, refetchNotifications]);

  return {
    // State
    notifications,
    unreadCount,
    isLoading,
    error,
    isOpen,

    // Actions
    openNotifications,
    closeNotifications,
    handleNotificationClick,
    markAllAsRead: markAllAsReadMutation.mutate,
    markAsRead: markAsReadMutation.mutate,

    // Mutations state
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isMarkingAsRead: markAsReadMutation.isPending,

    // Refetch functions
    refetchUnreadCount,
    refetchNotifications,
  };
};
